import { Bar, BarChart, CartesianGrid, Legend, Line, ComposedChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  BoardShell,
  ChartTooltip,
  DataTable,
  KpiGrid,
  LEGEND,
  NoteList,
  Panel,
  Section,
  fmtMoneyM,
  fmtNum,
  fmtPct,
  signedPct,
  type DataColumn,
  type KpiItem,
} from '@/components/qxo/qxoBoardPrimitives';
import { PLATFORM_BASELINE, SHARE_COUNT } from '@/components/qxo/qxoFilingsData';
import {
  institutionalSharePct,
  quarterLabel,
  yearLabel,
  type StreetEarningsRow,
  type StreetEstimate,
  type StreetFiling,
  type StreetGrade,
  type StreetInsiderTx,
  type StreetSnapshot,
  type StreetState,
} from '@/components/qxo/qxoStreet';

const MGMT_2030_EBITDA_B = PLATFORM_BASELINE.reduce((s, p) => s + p.target2030EbitdaB, 0);

const toM = (v: number | null | undefined) => (v == null ? null : v / 1e6);

const ESTIMATE_COLUMNS: DataColumn<StreetEstimate & { label: string }>[] = [
  { key: 'label', header: 'Period', render: (r) => r.label },
  { key: 'rev', header: 'Revenue (avg)', align: 'right', render: (r) => fmtMoneyM(toM(r.revenueAvg)) },
  { key: 'revRange', header: 'Low – high', align: 'right', render: (r) => `${fmtMoneyM(toM(r.revenueLow))} – ${fmtMoneyM(toM(r.revenueHigh))}` },
  { key: 'eps', header: 'EPS (avg)', align: 'right', render: (r) => (r.epsAvg == null ? '—' : `$${r.epsAvg.toFixed(2)}`) },
  { key: 'epsRange', header: 'Low – high', align: 'right', render: (r) => (r.epsLow == null || r.epsHigh == null ? '—' : `$${r.epsLow.toFixed(2)} – $${r.epsHigh.toFixed(2)}`) },
  { key: 'n', header: '# analysts', align: 'right', render: (r) => `${fmtNum(r.numAnalystsRevenue, 0)} / ${fmtNum(r.numAnalystsEps, 0)}` },
];

const EARNINGS_COLUMNS: DataColumn<StreetEarningsRow>[] = [
  { key: 'date', header: 'Reported', render: (r) => r.date ?? '—' },
  { key: 'rev', header: 'Revenue', align: 'right', render: (r) => fmtMoneyM(toM(r.revenueActual)) },
  { key: 'revEst', header: 'Est.', align: 'right', render: (r) => fmtMoneyM(toM(r.revenueEstimated)) },
  {
    key: 'revSurp',
    header: 'Surprise',
    align: 'right',
    render: (r) => (r.revenueSurprisePct == null ? '—' : signedPct(r.revenueSurprisePct)),
    tone: (r) => (r.revenueSurprisePct == null ? undefined : r.revenueSurprisePct < 0 ? 'text-rose-300' : 'text-emerald-300'),
  },
  { key: 'eps', header: 'EPS', align: 'right', render: (r) => (r.epsActual == null ? '—' : `$${r.epsActual.toFixed(2)}`) },
  { key: 'epsEst', header: 'Est.', align: 'right', render: (r) => (r.epsEstimated == null ? '—' : `$${r.epsEstimated.toFixed(2)}`) },
  {
    key: 'epsSurp',
    header: 'Δ',
    align: 'right',
    render: (r) => (r.epsSurprise == null ? '—' : `${r.epsSurprise > 0 ? '+' : ''}${r.epsSurprise.toFixed(2)}`),
    tone: (r) => (r.epsSurprise == null ? undefined : r.epsSurprise < 0 ? 'text-rose-300' : 'text-emerald-300'),
  },
];

const GRADE_COLUMNS: DataColumn<StreetGrade>[] = [
  { key: 'date', header: 'Date', render: (r) => r.date ?? '—' },
  { key: 'firm', header: 'Firm', render: (r) => r.firm ?? '—' },
  { key: 'action', header: 'Action', render: (r) => r.action ?? '—' },
  { key: 'grade', header: 'Rating', render: (r) => (r.previousGrade && r.previousGrade !== r.newGrade ? `${r.previousGrade} → ${r.newGrade}` : r.newGrade ?? '—') },
];

const INSIDER_COLUMNS: DataColumn<StreetInsiderTx>[] = [
  { key: 'date', header: 'Date', render: (r) => r.date ?? '—' },
  { key: 'name', header: 'Insider', render: (r) => r.name ?? '—' },
  { key: 'kind', header: 'Type', render: (r) => r.kind.replace(/_/g, ' ') },
  { key: 'shares', header: 'Shares', align: 'right', render: (r) => (r.shares == null ? '—' : fmtNum(r.shares, 0)) },
  { key: 'price', header: 'Price', align: 'right', render: (r) => (r.price ? `$${r.price.toFixed(2)}` : '—') },
];

const FILING_COLUMNS: DataColumn<StreetFiling>[] = [
  { key: 'date', header: 'Filed', render: (r) => r.date },
  { key: 'form', header: 'Form', render: (r) => r.form ?? '—' },
  {
    key: 'link',
    header: 'EDGAR',
    render: (r) =>
      r.link ? (
        <a href={r.link} target="_blank" rel="noreferrer" className="text-sky-300 underline-offset-2 hover:underline">
          open
        </a>
      ) : (
        '—'
      ),
  },
];

function buildKpis(s: StreetSnapshot): KpiItem[] {
  const q = s.quote;
  const c = s.consensus;
  const fy30 = s.estimatesAnnual.find((e) => e.date?.startsWith('2030'));
  const instPct = institutionalSharePct(s.ownership, SHARE_COUNT.commonOutstandingM);
  const buys = s.insiders.tallies.open_market_buy;
  const sells = s.insiders.tallies.open_market_sell;
  return [
    {
      id: 'px',
      label: 'Price',
      value: q.price == null ? '—' : `$${q.price.toFixed(2)}`,
      hint: `${q.changePct == null ? '' : `${signedPct(q.changePct)} today. `}52-week ${q.range52w ?? '—'}. Beta ${fmtNum(q.beta, 1)}.`,
      tone: 'neutral',
    },
    {
      id: 'pt',
      label: 'Street target',
      value: c.targetConsensus == null ? '—' : `$${c.targetConsensus.toFixed(0)}`,
      hint: `Range $${fmtNum(c.targetLow, 0)}–$${fmtNum(c.targetHigh, 0)}; ${c.impliedUpsidePct == null ? '' : `${signedPct(c.impliedUpsidePct, 0)} implied`}. ${s.grades.length} rating actions in the last year, all holds/maintains at Buy.`,
      tone: 'success',
    },
    {
      id: 'fy30',
      label: 'Street 2030 vs plan',
      value: fy30?.ebitdaAvg == null ? '—' : `${fmtMoneyM(toM(fy30.ebitdaAvg))} vs $${MGMT_2030_EBITDA_B.toFixed(1)}B`,
      hint: `FMP-sourced 2030 EBITDA consensus vs management's $${MGMT_2030_EBITDA_B.toFixed(1)}B organic target. Street 2030 EPS $${fmtNum(fy30?.epsAvg, 2)} → ${q.price && fy30?.epsAvg ? (q.price / fy30.epsAvg).toFixed(1) : '—'}x on today's price.`,
      tone: 'warning',
    },
    {
      id: 'inst',
      label: '13F holders',
      value: `${fmtNum(s.ownership.investorsHolding, 0)} · ${fmtPct(instPct, 0)}`,
      hint: `Filed ${s.ownership.date ?? '—'}. ${fmtNum(toM(s.ownership.numberOf13Fshares), 0)}M shares reported (${s.ownership.numberOf13FsharesChange == null ? '' : `${s.ownership.numberOf13FsharesChange > 0 ? '+' : ''}${fmtNum(toM(s.ownership.numberOf13FsharesChange), 0)}M q/q`}). ${fmtNum(s.ownership.newPositions, 0)} new, ${fmtNum(s.ownership.closedPositions, 0)} closed.`,
      tone: 'neutral',
    },
    {
      id: 'insiders',
      label: 'Insider open-market',
      value: `${buys} buys · ${sells} sells`,
      hint: `Last ${Object.values(s.insiders.tallies).reduce((a, b) => a + b, 0)} Form 4 lines: ${s.insiders.tallies.award_or_vest} awards/vests, ${s.insiders.tallies.tax_withholding} tax withholdings. Nobody is buying stock with their own cash at these prices.`,
      tone: buys === 0 ? 'warning' : 'success',
    },
    {
      id: 'next',
      label: 'Next print',
      value: s.earnings.upcoming?.date ?? '—',
      hint: `Q3 26: first quarter with TopBuild. Street ${fmtMoneyM(toM(s.earnings.upcoming?.revenueEstimated))} revenue, $${fmtNum(s.earnings.upcoming?.epsEstimated, 2)} EPS.`,
      tone: 'neutral',
    },
  ];
}

function StreetBody({ s }: { s: StreetSnapshot }) {
  const quarterly = s.estimatesQuarterly.map((e) => ({ ...e, label: quarterLabel(e.date) }));
  const annual = s.estimatesAnnual.map((e) => ({ ...e, label: yearLabel(e.date) }));
  const annualChart = annual.map((e) => ({
    period: e.label,
    revenueB: e.revenueAvg == null ? null : e.revenueAvg / 1e9,
    eps: e.epsAvg,
  }));
  const spot = s.quote.price;
  const lastReported = s.earnings.history[0]?.date ?? '';
  const nextPeriod = quarterly.find((q) => (q.date ?? '') > lastReported);

  return (
    <>
      <KpiGrid items={buildKpis(s)} />

      <Section title="Consensus">
        The street has QXO at ${fmtNum(s.consensus.targetConsensus, 0)} with every covering analyst at Buy. Revenue
        estimates step up with each closed deal; EPS estimates are the number to argue with because they sit on top
        of the fixed charges on the capital board.
      </Section>

      <div className="grid min-w-0 grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-2">
        <Panel datasetId="annual-consensus"
          tall
          title="Annual consensus: revenue and EPS"
          unit="Revenue $B (bars), EPS $ (line)"
          takeaway={`FY26 ${fmtMoneyM(toM(annual[1]?.revenueAvg))} → FY30 ${fmtMoneyM(toM(annual.at(-1)?.revenueAvg))} revenue; EPS $${fmtNum(annual[1]?.epsAvg, 2)} → $${fmtNum(annual.at(-1)?.epsAvg, 2)}. Management talks about $50B of revenue by 2030; the street models ~$33B.`}
          note="FMP analyst-estimates; 2029–2030 EPS rests on one or two contributors. Revenue analyst counts are shown in the table."
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={annualChart} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="rev" tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} unit="B" />
              <YAxis yAxisId="eps" orientation="right" tick={{ fill: '#86efac', fontSize: 10 }} axisLine={false} tickLine={false} unit="$" />
              <Tooltip content={<ChartTooltip formatValue={(v, name) => (name.includes('EPS') ? `$${v.toFixed(2)}` : `$${v.toFixed(1)}B`)} />} />
              <Bar yAxisId="rev" dataKey="revenueB" name="Revenue consensus" fill="#38bdf8" radius={[3, 3, 0, 0]} />
              <Line yAxisId="eps" type="monotone" dataKey="eps" name="EPS consensus" stroke="#86efac" strokeWidth={2} dot={{ r: 3, fill: '#86efac' }} />
              <Legend wrapperStyle={LEGEND} />
            </ComposedChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="quarterly-consensus"
          tall
          fixedHeight={false}
          title="Quarterly consensus"
          unit="Next eight quarters, FMP"
          takeaway="Q3 26 is the first consolidated TopBuild quarter and the street's first ~$4.9B revenue print. Q4 and Q1 are the seasonal trough — the same shape that produced $1M of EBITDA in Q1 26."
        >
          <DataTable rows={quarterly} columns={ESTIMATE_COLUMNS} rowKey={(r) => r.date ?? r.label} dense highlightRow={(r) => r.date === nextPeriod?.date} />
        </Panel>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-2">
        <Panel datasetId="prints-vs-estimates"
          tall
          fixedHeight={false}
          title="Prints vs estimates"
          unit="As FMP recorded them"
          takeaway="Revenue has beaten every quarter since Beacon closed; EPS missed in Q1 26 and landed on the number in Q2 26. Beats are on the top line, not the bottom."
          note="EPS here is the adjusted figure FMP records against consensus."
        >
          <DataTable rows={s.earnings.history.slice(0, 6)} columns={EARNINGS_COLUMNS} rowKey={(r) => r.date ?? ''} dense />
        </Panel>

        <Panel
          datasetId="annual-consensus"
          overviewOnly
          tall
          fixedHeight={false}
          title="Annual consensus detail"
          unit="FMP analyst-estimates, annual"
          takeaway={`Consensus wants ${fmtMoneyM(toM(annual.at(-1)?.revenueAvg))} of 2030 revenue from ${fmtMoneyM(toM(annual[0]?.revenueAvg))} in 2025 — a ${annual[0]?.revenueAvg && annual.at(-1)?.revenueAvg ? ((Math.pow(annual.at(-1)!.revenueAvg! / annual[0].revenueAvg, 1 / 5) - 1) * 100).toFixed(0) : '—'}% CAGR that already assumes more acquisitions.`}
        >
          <DataTable rows={annual} columns={ESTIMATE_COLUMNS} rowKey={(r) => r.date ?? r.label} dense />
        </Panel>
      </div>

      <Section title="Ownership, insiders, ratings">
        {`${fmtNum(s.ownership.investorsHolding, 0)} institutions reported ${fmtNum(toM(s.ownership.numberOf13Fshares), 0)}M shares for ${s.ownership.date ?? '—'} — roughly ${fmtPct(institutionalSharePct(s.ownership, SHARE_COUNT.commonOutstandingM), 0)} of the basic count. Put/call open interest in 13F filings is ${fmtNum(s.ownership.putCallRatio, 2)}.`}
      </Section>

      <div className="grid min-w-0 grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-3">
        <Panel datasetId="ownership-13f"
          fixedHeight={false}
          title="13F position changes"
          unit={`Quarter ended ${s.ownership.date ?? '—'}`}
          takeaway={`${fmtNum(s.ownership.increasedPositions, 0)} increased, ${fmtNum(s.ownership.reducedPositions, 0)} reduced, ${fmtNum(s.ownership.newPositions, 0)} new, ${fmtNum(s.ownership.closedPositions, 0)} closed.`}
          note="FMP's own ownership % uses a stale share count and exceeds 100%; the percentage in the header uses the 10-Q cover count instead."
        >
          <ResponsiveContainer width="100%" height={160}>
            <BarChart
              data={[
                { k: 'New', v: s.ownership.newPositions },
                { k: 'Increased', v: s.ownership.increasedPositions },
                { k: 'Reduced', v: s.ownership.reducedPositions },
                { k: 'Closed', v: s.ownership.closedPositions },
              ]}
              margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
            >
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="k" tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip formatValue={(v) => fmtNum(v, 0)} />} />
              <ReferenceLine y={0} stroke="#64748b" />
              <Bar dataKey="v" name="Holders" fill="#38bdf8" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="insiders"
          fixedHeight={false}
          title="Insider transactions"
          unit="Form 4, most recent"
          takeaway={`${s.insiders.tallies.open_market_buy} open-market buys and ${s.insiders.tallies.open_market_sell} sells in the window. The rest is equity comp vesting and tax withholding.`}
        >
          <DataTable rows={s.insiders.transactions.filter((t) => t.kind !== 'award_or_vest').slice(0, 8)} columns={INSIDER_COLUMNS} rowKey={(r) => `${r.date}-${r.name}-${r.shares}`} dense />
        </Panel>

        <Panel datasetId="grades"
          fixedHeight={false}
          title="Rating actions"
          unit="Last twelve months"
          takeaway={`${s.grades.length} actions, ${s.grades.filter((g) => g.action === 'maintain').length} of them maintains. Nobody has downgraded through a 55% drawdown${spot != null ? ` to $${spot.toFixed(2)}` : ''}.`}
        >
          <DataTable rows={s.grades.slice(0, 8)} columns={GRADE_COLUMNS} rowKey={(r) => `${r.date}-${r.firm}`} dense />
        </Panel>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-2">
        <Panel datasetId="sec-filings"
          fixedHeight={false}
          title="Recent SEC filings"
          unit="Last 400 days, via FMP"
          takeaway={`${Object.entries(s.filingCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([form, n]) => `${n} ${form}`)
            .join(', ')}. Watch for the Q3 26 10-Q (first TopBuild consolidation) and any S-3/424B (more equity).`}
        >
          <DataTable rows={s.filings.slice(0, 12)} columns={FILING_COLUMNS} rowKey={(r) => `${r.date}-${r.form}-${r.link}`} dense />
        </Panel>
        <NoteList
          title="How to read the street data"
          subtitle="Live from FMP through the backend; cached 12 hours."
          items={[
            { head: 'Price targets and grades', body: 'FMP price-target-consensus and grades endpoints. Sell-side coverage is uniformly positive; treat the target as a sentiment reading, not a valuation.', badge: 'FMP' },
            { head: 'Estimates', body: 'FMP analyst-estimates. Revenue counts 13–16 contributors; 2029–2030 EPS has one. EBITDA consensus from FMP is inconsistent with the quarterly path and is only shown in the 2030 headline.', badge: 'FMP · low confidence' },
            { head: '13F summary', body: 'FMP institutional-ownership symbol-positions-summary for the latest quarter whose 45-day deadline has passed. Share count denominator replaced with the 10-Q cover.', badge: 'FMP · 13F' },
            { head: 'Insiders', body: 'FMP insider-trading search classified into open-market buy/sell, awards/vests, tax withholding. Only P-Purchase and S-Sale codes count as conviction.', badge: 'Form 4' },
            { head: 'Refresh', body: `Snapshot generated ${new Date(s.generatedAt).toLocaleString()}. Backend: GET /api/street/QXO?refresh=true rebuilds it.`, badge: 'cache' },
          ]}
        />
      </div>
    </>
  );
}

export function QxoStreetPanel({ state }: { state: StreetState }) {
  return (
    <BoardShell
      id="street"
      title="QXO"
      subtitle="Street, ownership and filings feed"
      blurb="What the sell side, the 13F crowd and insiders are doing, pulled live from FMP through the backend. Read it against the filings boards above: consensus assumes the margin recovery that has not shown up yet."
      asOf={state.status === 'ready' ? `FMP snapshot ${new Date(state.data.generatedAt).toLocaleDateString()}` : 'FMP snapshot'}
      sourceLine="FMP stable API via /api/street/QXO"
      footer="Source: Financial Modeling Prep (profile, shares-float, price-target-consensus, grades, earnings, analyst-estimates, institutional-ownership, insider-trading, sec-filings-search) normalized in backend/services/street_desk. Filed numbers on the other boards take precedence where they disagree."
    >
      {state.status === 'loading' ? (
        <p className="px-5 py-8 text-[13px] text-sky-300">Loading street snapshot…</p>
      ) : state.status === 'error' ? (
        <div className="px-5 py-8 text-[13px] text-rose-300">
          <p>Street snapshot unavailable: {state.error}</p>
          <p className="mt-1 text-sky-400">Start the backend (start_backend.sh) or check FMP_API_KEY. The filings boards above do not depend on it.</p>
        </div>
      ) : (
        <StreetBody s={state.data} />
      )}
    </BoardShell>
  );
}
